/**
 * HtmlBridgePlayer -- protocol v3
 *
 * Renders any compliant HTML theme file inside a sandboxed <iframe> on web.
 * The HTML file communicates with the host via the postMessage bridge protocol
 * documented in docs/html-theme-structure.md.
 *
 * On native (iOS/Android) this component renders a "not supported" placeholder
 * because there is no react-native-webview bridge built yet.
 *
 * -- v3 Protocol --------------------------------------------------------------
 *
 *  iframe -> host
 *    { type: "ready" }
 *    { type: "answer",        payload: { slideId, value, timeSpentMs } }
 *    { type: "navigate_next" }
 *    { type: "navigate_prev" }
 *    { type: "navigate_to",   payload: { index } }
 *    { type: "complete",      payload: { answers?, totalTimeMs? } }
 *    { type: "exit" }                          <- theme finished showing results
 *
 *  host -> iframe
 *    { type: "init",          payload: BridgeInitPayload }
 *    { type: "slide_show",    payload: { index, slide, total } }
 *    { type: "feedback",      payload: BridgeFeedbackPayload }
 *    { type: "result",        payload: BridgeResultPayload }
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'

import {
  type Slide,
  SLIDE_TYPE_META,
  type ThemePlayerProps,
} from '#/types/capsule'

// -- Bridge types -------------------------------------------------------------

/** Normalised slide shape sent to the HTML theme. */
export interface BridgeSlide {
  id: string
  type: string
  index: number
  isExercise: boolean
  points: number
  timing?: number
  explanation?: string

  // Video
  videoUri?: string
  videoThumbnailUri?: string

  // Content / image
  contentTitle?: string
  contentBody?: string
  imageUri?: string
  imageCaption?: string

  // Text block
  textTitle?: string
  textBody?: string

  // Table
  tableRows?: string[][]
  tableCols?: number

  // Chart
  chartVariant?: 'bar' | 'line' | 'pie'
  chartData?: {label: string; value: number}[]

  // Formula
  latex?: string

  // Exercise common
  question?: string

  // single_choice / multiple_choice / image_choice
  options?: {id: string; text: string; isCorrect: boolean; imageUri?: string}[]

  // true_false
  correctAnswer?: 'true' | 'false'

  // fill_blank
  blanks?: {
    id: string
    before: string
    answer: string
    after: string
    alternatives?: string[]
  }[]

  // matching
  pairs?: {id: string; left: string; right: string}[]

  // ordering
  orderItems?: {id: string; label: string}[]
  correctOrder?: string[]

  // accordion
  accordionItems?: {id: string; title: string; body: string}[]

  // drag_words
  dragWordsTemplate?: string
  dragWordsBank?: string[]

  // fill_blanks (inline paragraph)
  fillBlanksParagraph?: string
  fillBlanksAnswers?: {id: string; answer: string; alternatives?: string[]}[]

  // find_words
  findWordsTargets?: string[]
  findWordsGrid?: string[][]

  // mark_words
  markWordsText?: string
  markWordsCorrect?: string[]

  // complex_fill_blank
  complexBlanks?: {
    id: string
    sentence: string
    answer: string
    options: string[]
    alternatives?: string[]
  }[]
}

/** Full init payload sent once the iframe posts "ready". */
export interface BridgeInitPayload {
  protocolVersion: 3
  mode: 'play' | 'preview' | 'creator'
  locale: string
  isRtl: boolean
  user: {
    id: string
    handle: string
    displayName: string
    avatarUri: string | null
  }
  behaviour: {
    instantFeedback: boolean
    shuffleOptions: boolean
    showProgressBar: boolean
  }
  capsule: {
    id: string
    title: string
    description: string
    authorHandle: string
    authorDisplayName: string
    coverImageUri?: string
  }
  slides: BridgeSlide[]
  totalSlides: number
  totalPoints: number
}

/** Feedback sent to the iframe after it reports an answer. */
export interface BridgeFeedbackPayload {
  slideId: string
  correct: boolean
  pointsEarned: number // 0 if wrong
  pointsPossible: number
  explanation?: string
  /** The correct value -- type depends on slide type, same shape the theme should have submitted */
  correctValue?: unknown
}

/** Final result sent to the iframe after it posts "complete". */
export interface BridgeResultPayload {
  totalPoints: number
  maxPoints: number
  percentage: number // 0-100
  correctCount: number
  totalExerciseCount: number
  totalTimeMs: number
  answers: {
    slideId: string
    correct: boolean
    pointsEarned: number
    value: unknown
    timeSpentMs: number
  }[]
}

// -- Answer shape from theme --------------------------------------------------

interface AnswerMsg {
  slideId: string
  value: unknown
  timeSpentMs: number
}

interface ComputedAnswer extends AnswerMsg {
  correct: boolean
  pointsEarned: number
}

// -- Answer validator ---------------------------------------------------------

export function checkAnswer(slide: BridgeSlide, value: unknown): boolean {
  switch (slide.type) {
    case 'single_choice':
    case 'image_choice': {
      const correct = slide.options?.find(o => o.isCorrect)
      return correct?.id === value
    }
    case 'multiple_choice': {
      const correctIds = new Set(
        slide.options?.filter(o => o.isCorrect).map(o => o.id) ?? [],
      )
      const selected = new Set(Array.isArray(value) ? (value as string[]) : [])
      if (correctIds.size !== selected.size) return false
      for (const id of correctIds) if (!selected.has(id)) return false
      return true
    }
    case 'true_false': {
      return slide.correctAnswer === String(value)
    }
    case 'matching': {
      // value: { [leftId]: rightId }
      const map = (value as Record<string, string>) ?? {}
      return (slide.pairs ?? []).every(
        p => map[p.id] === p.right || map[p.id] === p.id,
      )
    }
    case 'ordering': {
      const correct = slide.correctOrder ?? []
      const submitted = Array.isArray(value) ? (value as string[]) : []
      return (
        correct.length === submitted.length &&
        correct.every((id, i) => id === submitted[i])
      )
    }
    case 'drag_words': {
      const map = (value as Record<string, string>) ?? {}
      const template = slide.dragWordsTemplate ?? ''
      let idx = 0
      let allCorrect = true
      template.replace(/\[([^\]]+)\]/g, (_, word: string) => {
        const blankId = `blank-${++idx}`
        if (map[blankId] !== word) allCorrect = false
        return ''
      })
      return allCorrect
    }
    case 'fill_blanks': {
      const vals = Array.isArray(value) ? (value as string[]) : []
      return (slide.fillBlanksAnswers ?? []).every((a, i) => {
        const v = (vals[i] ?? '').trim().toLowerCase()
        return (
          v === a.answer.toLowerCase() ||
          (a.alternatives ?? []).some(alt => alt.toLowerCase() === v)
        )
      })
    }
    case 'find_words': {
      const found = new Set(Array.isArray(value) ? (value as string[]) : [])
      return (slide.findWordsTargets ?? []).every(t => found.has(t))
    }
    case 'mark_words': {
      const selected = new Set(Array.isArray(value) ? (value as string[]) : [])
      const correct = new Set(slide.markWordsCorrect ?? [])
      if (selected.size !== correct.size) return false
      for (const w of correct) if (!selected.has(w)) return false
      return true
    }
    case 'complex_fill_blank': {
      const vals = Array.isArray(value) ? (value as string[]) : []
      return (slide.complexBlanks ?? []).every((b, i) => {
        const v = (vals[i] ?? '').trim().toLowerCase()
        return (
          v === b.answer.toLowerCase() ||
          (b.alternatives ?? []).some(a => a.toLowerCase() === v)
        )
      })
    }
    default:
      return true // non-exercise slides
  }
}

/** The correct "model answer" value to send back as feedback */
export function correctValueFor(slide: BridgeSlide): unknown {
  switch (slide.type) {
    case 'single_choice':
    case 'image_choice':
      return slide.options?.find(o => o.isCorrect)?.id ?? null
    case 'multiple_choice':
      return slide.options?.filter(o => o.isCorrect).map(o => o.id) ?? []
    case 'true_false':
      return slide.correctAnswer ?? null
    case 'matching':
      return Object.fromEntries((slide.pairs ?? []).map(p => [p.id, p.right]))
    case 'ordering':
      return slide.correctOrder ?? []
    case 'drag_words': {
      const map: Record<string, string> = {}
      let idx = 0
      ;(slide.dragWordsTemplate ?? '').replace(
        /\[([^\]]+)\]/g,
        (_, word: string) => {
          map[`blank-${++idx}`] = word
          return ''
        },
      )
      return map
    }
    case 'fill_blanks':
      return (slide.fillBlanksAnswers ?? []).map(a => a.answer)
    case 'mark_words':
      return slide.markWordsCorrect ?? []
    case 'complex_fill_blank':
      return (slide.complexBlanks ?? []).map(b => b.answer)
    default:
      return null
  }
}

// -- Slide mapper -------------------------------------------------------------

export function mapSlide(sl: Slide, index: number): BridgeSlide {
  const meta = SLIDE_TYPE_META[sl.type]
  // Some slide fields are not yet in the base Slide type but exist at runtime
  // for newer slide types (table, chart, formula, fill_blanks). Cast once here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ext = sl as Record<string, any>
  return {
    id: sl.id,
    type: sl.type,
    index,
    isExercise: meta?.isExercise ?? false,
    points: sl.points ?? 0,
    ...(sl.timing !== undefined && {timing: sl.timing}),
    ...(sl.explanation && {explanation: sl.explanation}),

    // Video
    ...(sl.videoUrl && {videoUri: sl.videoUrl}),
    ...(sl.videoThumbnailUrl && {videoThumbnailUri: sl.videoThumbnailUrl}),

    // Content / image
    ...(sl.contentTitle && {contentTitle: sl.contentTitle}),
    ...(sl.contentText && {contentBody: sl.contentText}),
    ...(sl.imageUrl && {imageUri: sl.imageUrl}),

    // Exercise common
    ...(sl.question && {question: sl.question}),

    // Options
    ...(sl.options && {
      options: sl.options.map(o => ({
        id: o.id,
        text: o.label,
        isCorrect: o.isCorrect,
        ...(o.imageUrl && {imageUri: o.imageUrl}),
      })),
    }),

    // true_false
    ...(sl.answer && {correctAnswer: sl.answer as 'true' | 'false'}),

    // fill_blank
    ...(sl.blanks && {blanks: sl.blanks}),

    // matching
    ...(sl.pairs && {pairs: sl.pairs}),

    // ordering
    ...(sl.orderItems && {orderItems: sl.orderItems}),
    ...(sl.correctOrder && {correctOrder: sl.correctOrder}),

    // accordion
    ...(sl.accordionItems && {accordionItems: sl.accordionItems}),

    // drag_words
    ...(sl.dragWordsTemplate && {dragWordsTemplate: sl.dragWordsTemplate}),
    ...(sl.dragWordsBank && {dragWordsBank: sl.dragWordsBank}),

    // fill_blanks (fields not yet in base Slide type -- accessed via ext)
    ...(ext.fillBlanksParagraph && {
      fillBlanksParagraph: ext.fillBlanksParagraph,
    }),
    ...(ext.fillBlanksAnswers && {fillBlanksAnswers: ext.fillBlanksAnswers}),

    // find_words
    ...(sl.findWordsTargets && {findWordsTargets: sl.findWordsTargets}),
    ...(sl.findWordsGrid && {findWordsGrid: sl.findWordsGrid}),

    // mark_words
    ...(sl.markWordsText && {markWordsText: sl.markWordsText}),
    ...(sl.markWordsCorrect && {markWordsCorrect: sl.markWordsCorrect}),

    // complex_fill_blank
    ...(sl.complexBlanks && {complexBlanks: sl.complexBlanks}),

    // table_slide (fields not yet in base Slide type -- accessed via ext)
    ...(ext.tableRows && {tableRows: ext.tableRows}),
    ...(ext.tableCols !== undefined && {tableCols: ext.tableCols}),

    // chart_slide (fields not yet in base Slide type -- accessed via ext)
    ...(ext.chartVariant && {chartVariant: ext.chartVariant}),
    ...(ext.chartData && {chartData: ext.chartData}),

    // formula_slide (fields not yet in base Slide type -- accessed via ext)
    ...(ext.latex && {latex: ext.latex}),
  }
}

function buildInitPayload(
  props: ThemePlayerProps,
  bridgeSlides: BridgeSlide[],
  mode: 'play' | 'preview' | 'creator' = 'play',
): {type: 'init'; payload: BridgeInitPayload} {
  const {capsule} = props
  const settings = capsule.behaviorSettings ?? {}
  const totalPoints = bridgeSlides.reduce(
    (sum, s) => sum + (s.isExercise ? s.points : 0),
    0,
  )

  return {
    type: 'init',
    payload: {
      protocolVersion: 3,
      mode,
      locale: 'en',
      isRtl: false,
      user: {id: 'user', handle: '@user', displayName: 'User', avatarUri: null},
      behaviour: {
        instantFeedback: settings.instantFeedback !== false,
        shuffleOptions: Boolean(settings.shuffleOptions),
        showProgressBar: settings.showProgressBar !== false,
      },
      capsule: {
        id: capsule.id,
        title: capsule.title,
        description: capsule.description,
        authorHandle: '@capsule',
        authorDisplayName: 'Capsule',
      },
      slides: bridgeSlides,
      totalSlides: bridgeSlides.length,
      totalPoints,
    },
  }
}

// -- Web-only file picker -----------------------------------------------------

function pickHtmlFileWeb(): Promise<string | null> {
  return new Promise(resolve => {
    if (typeof document === 'undefined') {
      resolve(null)
      return
    }
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.html,text/html'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) {
        resolve(null)
        return
      }
      const text = await file.text()
      const blob = new Blob([text], {type: 'text/html'})
      resolve(URL.createObjectURL(blob))
    }
    input.oncancel = () => resolve(null)
    input.click()
  })
}

// -- Component ----------------------------------------------------------------

type PlayerState = 'idle' | 'loading' | 'running' | 'error'

export function HtmlBridgePlayer(
  props: ThemePlayerProps & {mode?: 'play' | 'preview' | 'creator'},
) {
  const {capsule, onCapsuleComplete} = props
  const bridgeMode = props.mode ?? 'play'
  const settings = capsule.behaviorSettings ?? {}

  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const blobUrlRef = useRef<string | null>(null)

  const customHtml = (settings?.customHtml as string) || null

  const [srcUrl, setSrcUrl] = useState<string | null>(() => {
    if (Platform.OS !== 'web') return null
    if (customHtml) {
      const url = URL.createObjectURL(
        new Blob([customHtml], {type: 'text/html'}),
      )
      blobUrlRef.current = url
      return url
    }
    return (settings?.htmlThemeUrl as string) || null
  })
  const [state, setState] = useState<PlayerState>(srcUrl ? 'loading' : 'idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [urlInput, setUrlInput] = useState(
    (settings?.htmlThemeUrl as string) || '',
  )
  const [busy, setBusy] = useState(false)

  // Accumulated computed answers
  const answersRef = useRef<ComputedAnswer[]>([])
  const sessionStartRef = useRef<number>(Date.now())

  // -- Stable refs so callbacks never go stale --------------------------------
  // initSentRef: guards against double-init (race condition: ready fires before
  // useEffect registers the listener, so onLoad sends a fallback init after 300 ms).
  const initSentRef = useRef(false)
  // slideIndexRef: lets navigate_next/prev read the current index without
  // having slideIndex in the handleMessage effect's dependency array (which
  // would tear down / re-add the listener on every slide advance).
  const slideIndexRef = useRef(0)

  // Memoise bridgeSlides and initPayload so their object identity is stable
  // across re-renders; this stops the handleMessage effect from being torn
  // down and re-added unnecessarily.
  const bridgeSlides = useMemo(
    () => (capsule.slides ?? []).map((sl, i) => mapSlide(sl, i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capsule.id, capsule.slides],
  )
  const initPayload = useMemo(
    () => buildInitPayload(props, bridgeSlides, bridgeMode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [capsule, bridgeSlides, bridgeMode],
  )

  // Keep "live" refs in sync so the stable handleMessage closure always reads
  // the latest values without needing them as dependencies.
  const bridgeSlidesRef = useRef(bridgeSlides)
  const initPayloadRef = useRef(initPayload)
  useEffect(() => {
    bridgeSlidesRef.current = bridgeSlides
  }, [bridgeSlides])
  useEffect(() => {
    initPayloadRef.current = initPayload
  }, [initPayload])

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current)
    }
  }, [])

  const sendToTheme = useCallback((msg: unknown) => {
    iframeRef.current?.contentWindow?.postMessage(msg, '*')
  }, [])

  // Keep a ref so the stable handleMessage closure can call sendToTheme
  const sendToThemeRef = useRef(sendToTheme)
  useEffect(() => {
    sendToThemeRef.current = sendToTheme
  }, [sendToTheme])

  // showSlide uses refs only -> stable identity, no deps needed.
  const showSlide = useCallback((index: number) => {
    const slides = bridgeSlidesRef.current
    if (index < 0 || index >= slides.length) return
    slideIndexRef.current = index
    sendToThemeRef.current({
      type: 'slide_show',
      payload: {index, slide: slides[index], total: slides.length},
    })
  }, [])

  /** Send init + slide 0 to the theme, guarded by initSentRef. */
  const sendInit = useCallback(() => {
    if (initSentRef.current) return
    initSentRef.current = true
    const payload = initPayloadRef.current
    const slides = bridgeSlidesRef.current
    sendToThemeRef.current(payload)
    // Explicitly send slide 0 so themes that don't auto-advance on init also work.
    if (slides.length > 0) {
      slideIndexRef.current = 0
      sendToThemeRef.current({
        type: 'slide_show',
        payload: {index: 0, slide: slides[0], total: slides.length},
      })
    }
  }, [])

  // postMessage bridge (web only).
  // Dependencies are minimal (only truly stable values) so the listener is
  // registered once and never torn down mid-flight.
  useEffect(() => {
    if (Platform.OS !== 'web') return

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      const msg = event.data as {type?: string; [k: string]: unknown}
      if (!msg || typeof msg.type !== 'string') return

      switch (msg.type) {
        // -- Theme ready -------------------------------------------------------
        // Reset initSentRef before calling sendInit so that if the iframe
        // reloads internally (e.g. hash navigation) the new "ready" event
        // can trigger a fresh init instead of being swallowed by the guard.
        case 'ready': {
          initSentRef.current = false
          sendInit()
          setState('running')
          sessionStartRef.current = Date.now()
          answersRef.current = []
          break
        }

        // -- Answer submitted by theme -----------------------------------------
        case 'answer': {
          const p = msg.payload as Partial<AnswerMsg>
          if (!p?.slideId) break

          const slides = bridgeSlidesRef.current
          const slide = slides.find(s => s.id === p.slideId)
          if (!slide) break

          const correct = slide.isExercise
            ? checkAnswer(slide, p.value ?? null)
            : true
          const pointsEarned = correct ? slide.points : 0

          const computed: ComputedAnswer = {
            slideId: p.slideId,
            value: p.value ?? null,
            timeSpentMs: p.timeSpentMs ?? 0,
            correct,
            pointsEarned,
          }

          // Deduplicate
          answersRef.current = [
            ...answersRef.current.filter(a => a.slideId !== p.slideId),
            computed,
          ]

          // Send feedback back to theme
          const feedback: {type: 'feedback'; payload: BridgeFeedbackPayload} = {
            type: 'feedback',
            payload: {
              slideId: p.slideId,
              correct,
              pointsEarned,
              pointsPossible: slide.points,
              explanation: slide.explanation,
              correctValue: correct ? undefined : correctValueFor(slide),
            },
          }
          sendToThemeRef.current(feedback)
          break
        }

        // -- Navigation --------------------------------------------------------
        // Use slideIndexRef (not stale closure state) so we always have the
        // current index regardless of when this handler fires.
        case 'navigate_next': {
          showSlide(slideIndexRef.current + 1)
          break
        }
        case 'navigate_prev': {
          showSlide(slideIndexRef.current - 1)
          break
        }
        case 'navigate_to': {
          const {index} = (msg.payload ?? {}) as {index?: number}
          if (typeof index === 'number') showSlide(index)
          break
        }

        // -- Session complete --------------------------------------------------
        case 'complete': {
          const p = (msg.payload ?? {}) as {
            answers?: AnswerMsg[]
            totalTimeMs?: number
          }
          const totalMs = p.totalTimeMs ?? Date.now() - sessionStartRef.current
          const slides = bridgeSlidesRef.current

          // If theme sent its own answers array, merge into our computed set
          if (Array.isArray(p.answers) && p.answers.length > 0) {
            for (const a of p.answers) {
              if (answersRef.current.some(c => c.slideId === a.slideId))
                continue
              const slide = slides.find(s => s.id === a.slideId)
              if (!slide) continue
              const correct = slide.isExercise
                ? checkAnswer(slide, a.value ?? null)
                : true
              answersRef.current.push({
                slideId: a.slideId,
                value: a.value ?? null,
                timeSpentMs: a.timeSpentMs ?? 0,
                correct,
                pointsEarned: correct ? slide.points : 0,
              })
            }
          }

          const finalAnswers = answersRef.current
          const exerciseSlides = slides.filter(s => s.isExercise)
          const maxPoints = exerciseSlides.reduce((sum, s) => sum + s.points, 0)
          const totalPoints = finalAnswers.reduce(
            (sum, a) => sum + a.pointsEarned,
            0,
          )
          const correctCount = finalAnswers.filter(a => a.correct).length
          const percentage =
            maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 100

          const result: {type: 'result'; payload: BridgeResultPayload} = {
            type: 'result',
            payload: {
              totalPoints,
              maxPoints,
              percentage,
              correctCount,
              totalExerciseCount: exerciseSlides.length,
              totalTimeMs: totalMs,
              answers: finalAnswers.map(a => ({
                slideId: a.slideId,
                correct: a.correct,
                pointsEarned: a.pointsEarned,
                value: a.value,
                timeSpentMs: a.timeSpentMs,
              })),
            },
          }

          // Send result to theme so it can show its own result/completion screen
          sendToThemeRef.current(result)
          break
        }

        // -- Theme signals it's done (after showing its own result screen) -----
        case 'exit': {
          const finalAnswers = answersRef.current
          const totalMs = Date.now() - sessionStartRef.current
          const perSliceMs =
            finalAnswers.length > 0
              ? Math.round(totalMs / finalAnswers.length)
              : totalMs

          onCapsuleComplete(
            finalAnswers.map(a => ({
              pageId: a.slideId,
              isCorrect: Boolean(a.correct),
              timeSpentMs: a.timeSpentMs > 0 ? a.timeSpentMs : perSliceMs,
              userAnswer: a.value,
            })),
          )
          break
        }

        default:
          break
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
    // showSlide and sendInit are stable (no deps in their useCallback).
    // onCapsuleComplete is the only external dep that can legitimately change.
  }, [onCapsuleComplete, showSlide, sendInit])

  const loadUrl = useCallback((url: string) => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }
    // Reset session so the new theme goes through full init handshake.
    initSentRef.current = false
    slideIndexRef.current = 0
    setSrcUrl(url)
    setState('loading')
    setErrorMsg(null)
    answersRef.current = []
  }, [])

  // When the capsule's htmlThemeUrl changes in props (e.g. teacher switches between
  // two built-in HTML themes from the ThemePicker while already on html-bridge),
  // reload the iframe so the new theme is shown without unmounting/remounting.
  const prevHtmlThemeUrlRef = useRef(
    settings?.htmlThemeUrl as string | undefined,
  )
  useEffect(() => {
    if (Platform.OS !== 'web') return
    const newUrl = (settings?.htmlThemeUrl as string) || undefined
    if (!customHtml && newUrl && newUrl !== prevHtmlThemeUrlRef.current) {
      prevHtmlThemeUrlRef.current = newUrl
      loadUrl(newUrl)
    }
  }, [settings?.htmlThemeUrl, customHtml, loadUrl])

  const handlePickFile = async () => {
    setBusy(true)
    setErrorMsg(null)
    try {
      const url = await pickHtmlFileWeb()
      if (url) {
        blobUrlRef.current = url
        loadUrl(url)
      }
    } catch {
      setErrorMsg('Could not load the HTML file.')
    } finally {
      setBusy(false)
    }
  }

  const handleLoadUrl = () => {
    const trimmed = urlInput.trim()
    if (!trimmed) return
    if (
      !trimmed.startsWith('http://') &&
      !trimmed.startsWith('https://') &&
      !trimmed.startsWith('blob:')
    ) {
      setErrorMsg('Enter a valid https:// URL.')
      return
    }
    loadUrl(trimmed)
  }

  const handleIframeError = () => {
    setState('error')
    setErrorMsg('Failed to load the theme. Check the URL or file.')
  }

  // -- Native: not supported -------------------------------------------------
  if (Platform.OS !== 'web') {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🌐</Text>
        <Text style={s.title}>HTML Theme</Text>
        <Text style={s.desc}>
          HTML/CSS/JS themes render in the web version.{'\n'}
          On iOS/Android, choose a different theme.
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          style={s.btn}
          onPress={() => onCapsuleComplete([])}>
          <Text style={s.btnTxt}>Exit</Text>
        </TouchableOpacity>
      </View>
    )
  }

  // -- Configured theme: fullscreen iframe -- no host chrome -----------------
  // When the capsule already has a theme (customHtml / htmlThemeUrl), the iframe
  // fills the entire screen. The theme's own HTML owns all UI -- no toolbar, no
  // host buttons. This covers student play (mode="play"), teacher full-capsule
  // preview (mode="preview"), and single-slide creator preview (mode="creator").
  const isConfigured = !!(customHtml || (settings?.htmlThemeUrl as string))

  if (isConfigured) {
    if (state === 'error') {
      return (
        <View style={s.placeholder}>
          <Text style={s.icon}>⚠️</Text>
          <Text style={s.title}>Theme failed to load</Text>
          <Text style={s.desc}>
            {errorMsg ?? 'The HTML theme could not be loaded.'}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            style={s.btn}
            onPress={() => onCapsuleComplete([])}>
            <Text style={s.btnTxt}>Exit</Text>
          </TouchableOpacity>
        </View>
      )
    }
    return (
      <View style={s.root}>
        {/* Spinner overlay until the theme fires "ready" -- then disappears */}
        {state === 'loading' && (
          <View style={s.loadingOverlay} pointerEvents="none">
            <ActivityIndicator size="large" color="#14b8a6" />
          </View>
        )}
        {/* @ts-ignore -- web-only element */}
        <iframe
          ref={iframeRef}
          src={srcUrl ?? ''}
          style={{width: '100%', flex: 1, border: 'none'}}
          sandbox="allow-scripts allow-same-origin allow-forms"
          allow="fullscreen"
          allowFullScreen
          title="HTML Theme Player"
          onLoad={() => {
            if (state === 'loading') setState('running')
            // Race-condition fallback: blob URLs can fire `ready` before
            // React registers the message listener. Send init proactively
            // if initSentRef is still false 300 ms after load.
            setTimeout(() => {
              if (!initSentRef.current && iframeRef.current?.contentWindow) {
                initSentRef.current = true
                const payload = initPayloadRef.current
                const slides = bridgeSlidesRef.current
                iframeRef.current.contentWindow.postMessage(payload, '*')
                if (slides.length > 0) {
                  slideIndexRef.current = 0
                  iframeRef.current.contentWindow.postMessage(
                    {
                      type: 'slide_show',
                      payload: {
                        index: 0,
                        slide: slides[0],
                        total: slides.length,
                      },
                    },
                    '*',
                  )
                }
              }
            }, 300)
          }}
          onError={handleIframeError}
        />
      </View>
    )
  }

  // -- No pre-configured theme -----------------------------------------------

  // In preview / play mode the capsule should always have a theme attached.
  // Show a clear error instead of the dev file-picker so the teacher knows
  // they need to go back and upload an HTML file from the Studio screen.
  if (bridgeMode === 'preview' || bridgeMode === 'play') {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🎨</Text>
        <Text style={s.title}>No HTML theme configured</Text>
        <Text style={s.desc}>
          This capsule uses the HTML Bridge theme but no .html file is attached.
          {'\n\n'}
          Delete this capsule and create a new one from the Studio screen --
          select "HTML Theme" and upload your .html file before the editor
          opens.
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          style={s.btn}
          onPress={() => onCapsuleComplete([])}>
          <Text style={s.btnTxt}>← Go back</Text>
        </TouchableOpacity>
      </View>
    )
  }

  // -- Dev / standalone testing: show picker + URL input ---------------------
  if (state === 'idle' || !srcUrl) {
    return (
      <View style={s.placeholder}>
        <Text style={s.icon}>🎨</Text>
        <Text style={s.title}>Load an HTML Theme</Text>
        <Text style={s.desc}>
          Pick a local .html file or enter a hosted theme URL.{'\n'}
          The file runs sandboxed -- nothing is uploaded to a server.
        </Text>

        {errorMsg ? <Text style={s.error}>{errorMsg}</Text> : null}

        <View style={s.urlRow}>
          <TextInput
            accessibilityLabel="HTML theme URL"
            accessibilityHint="Enter the URL of a hosted HTML theme"
            style={s.urlInput}
            value={urlInput}
            onChangeText={setUrlInput}
            placeholder="https://my-theme.example.com/theme.html"
            placeholderTextColor="rgba(255,255,255,0.3)"
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={handleLoadUrl}
            returnKeyType="go"
          />
          <TouchableOpacity
            accessibilityRole="button"
            style={[s.urlBtn, !urlInput.trim() && s.urlBtnDisabled]}
            onPress={handleLoadUrl}
            disabled={!urlInput.trim() || busy}>
            <Text style={s.urlBtnTxt}>Load URL</Text>
          </TouchableOpacity>
        </View>

        <Text style={s.orSep}>-- or --</Text>

        <TouchableOpacity
          accessibilityRole="button"
          style={s.btn}
          onPress={handlePickFile}
          disabled={busy}>
          {busy ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={s.btnTxt}>📂 Choose local HTML file</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          accessibilityRole="button"
          style={s.secondaryBtn}
          onPress={() => onCapsuleComplete([])}>
          <Text style={s.secondaryBtnTxt}>Cancel</Text>
        </TouchableOpacity>
      </View>
    )
  }

  // -- Dev mode: theme loaded via picker / URL input -------------------------
  const isBlob = srcUrl.startsWith('blob:')

  return (
    <View style={s.root}>
      <View style={s.toolbar}>
        <Text style={s.toolbarTitle} numberOfLines={1}>
          🎨 HTML Theme{isBlob ? ' (local)' : ''}
        </Text>
        {state === 'loading' && (
          <ActivityIndicator
            color="rgba(255,255,255,0.5)"
            size="small"
            style={s.toolbarSpinner}
          />
        )}
        <TouchableOpacity
          accessibilityRole="button"
          onPress={handlePickFile}
          style={s.changeBtn}
          disabled={busy}>
          {busy ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={s.changeBtnTxt}>Change</Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={() => onCapsuleComplete([])}
          style={s.closeBtn}>
          <Text style={s.closeBtnTxt}>✕</Text>
        </TouchableOpacity>
      </View>

      {state === 'error' && (
        <View style={s.errorBanner}>
          <Text style={s.errorBannerTxt}>
            {errorMsg ?? 'Theme failed to load.'}
          </Text>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => {
              setSrcUrl(null)
              setState('idle')
            }}
            style={s.errorAction}>
            <Text style={s.errorActionTxt}>Try again</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* @ts-ignore -- web-only element */}
      <iframe
        ref={iframeRef}
        src={srcUrl}
        style={{
          width: '100%',
          flex: 1,
          border: 'none',
          display: state === 'error' ? 'none' : 'block',
        }}
        sandbox="allow-scripts allow-same-origin allow-forms"
        allow="fullscreen"
        allowFullScreen
        title="HTML Theme Player"
        onLoad={() => {
          if (state === 'loading') setState('running')
          setTimeout(() => {
            if (!initSentRef.current && iframeRef.current?.contentWindow) {
              initSentRef.current = true
              const payload = initPayloadRef.current
              const slides = bridgeSlidesRef.current
              iframeRef.current.contentWindow.postMessage(payload, '*')
              if (slides.length > 0) {
                slideIndexRef.current = 0
                iframeRef.current.contentWindow.postMessage(
                  {
                    type: 'slide_show',
                    payload: {index: 0, slide: slides[0], total: slides.length},
                  },
                  '*',
                )
              }
            }
          }, 300)
        }}
        onError={handleIframeError}
      />
    </View>
  )
}

// -- Styles -------------------------------------------------------------------

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#0a1220',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    display: 'flex' as any,
    flexDirection: 'column',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    alignItems: 'center' as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    justifyContent: 'center' as any,
    backgroundColor: '#0a1220',
    zIndex: 10,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: '#0a1220',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.08)',
    gap: 8,
  },
  toolbarTitle: {
    flex: 1,
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
    fontWeight: '600',
  },
  toolbarSpinner: {marginHorizontal: 4},
  changeBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#0085ff',
  },
  changeBtnTxt: {color: '#fff', fontSize: 12, fontWeight: '700'},
  closeBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  closeBtnTxt: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 14,
    fontWeight: '700',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: 'rgba(239,68,68,0.18)',
    gap: 12,
  },
  errorBannerTxt: {flex: 1, color: '#f87171', fontSize: 13},
  errorAction: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: 'rgba(239,68,68,0.3)',
  },
  errorActionTxt: {color: '#fff', fontSize: 12, fontWeight: '700'},
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 36,
    gap: 14,
    backgroundColor: '#0a1220',
  },
  icon: {fontSize: 52},
  title: {color: '#fff', fontSize: 22, fontWeight: '800', textAlign: 'center'},
  desc: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 380,
  },
  urlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    maxWidth: 480,
    marginTop: 4,
  },
  urlInput: {
    flex: 1,
    height: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    color: '#fff',
    fontSize: 13,
  },
  urlBtn: {
    height: 44,
    paddingHorizontal: 16,
    borderRadius: 10,
    backgroundColor: '#0085ff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  urlBtnDisabled: {opacity: 0.4},
  urlBtnTxt: {color: '#fff', fontSize: 13, fontWeight: '700'},
  orSep: {color: 'rgba(255,255,255,0.3)', fontSize: 13},
  btn: {
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 22,
    backgroundColor: '#0085ff',
    minWidth: 220,
    alignItems: 'center',
  },
  btnTxt: {color: '#fff', fontSize: 15, fontWeight: '700'},
  secondaryBtn: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  secondaryBtnTxt: {color: 'rgba(255,255,255,0.45)', fontSize: 14},
  error: {
    color: '#f87171',
    fontSize: 13,
    textAlign: 'center',
    backgroundColor: 'rgba(239,68,68,0.13)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 10,
    maxWidth: 400,
  },
})
